import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, signal, viewChild } from '@angular/core';
import { GraphQLError } from 'graphql';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DataSelectorDirective } from '../../directives/data-selector/data-selector.directive';
import { FileModel } from '../../models';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { Logger } from '../../services/logger.service';
import { SharedModule } from '../../shared.module';
import { FilePreviewSidebarComponent } from './file-preview-sidebar/file-preview-sidebar.component';
import { BlobFetchProcessor } from './processors/blob-fetch.processor';
import { FILE_PREVIEW_PROCESSORS, FilePreviewProcessor, PreviewSource } from './processors/file-preview-processor';
import { FilePreviewProcessorFactory } from './processors/file-preview-processor.factory';
import { ApryseRendererComponent } from './renderers/apryse/apryse-renderer.component';
import { ErrorRendererComponent } from './renderers/error/error-renderer.component';
import { UnsupportedRendererComponent } from './renderers/unsupported/unsupported-renderer.component';
import { FilePreviewTelemetryService } from './telemetry/file-preview-telemetry.service';
import { LoadTrigger, RenderedMeta } from './telemetry/file-preview-telemetry.types';
import { getFileExtension, getRendererForExtension, isSupportedExtension } from './utils/file-extension.util';

export interface FilePreviewData {
  files: Partial<FileModel>[];
  initialIndex?: number;
  getFileUrl: (file: Partial<FileModel>) => Observable<string>;
  /** Secure links with VIEW_ONLY permission hide every way of taking the file (download + print). */
  canDownload?: boolean;
}

@Component({
  selector: 'app-file-preview',
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss'],
  imports: [
    SharedModule,
    MatProgressSpinnerModule,
    FilePreviewSidebarComponent,
    ApryseRendererComponent,
    ErrorRendererComponent,
    UnsupportedRendererComponent,
    DataSelectorDirective,
  ],
  providers: [
    FilePreviewTelemetryService,
    FilePreviewProcessorFactory,
    { provide: FILE_PREVIEW_PROCESSORS, useClass: BlobFetchProcessor, multi: true },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewComponent implements OnDestroy {
  private readonly _dialogRef = inject<MatDialogRef<FilePreviewComponent>>(MatDialogRef);
  private readonly _data = inject<FilePreviewData>(MAT_DIALOG_DATA);
  private readonly _logger = inject(Logger);
  private readonly _telemetry = inject(FilePreviewTelemetryService);
  private readonly _processorFactory = inject(FilePreviewProcessorFactory);

  protected readonly files = signal<Partial<FileModel>[]>(this._data.files);
  protected readonly currentIndex = signal<number>(this.clampInitialIndex(this._data.initialIndex ?? 0));
  protected readonly canDownload = this._data.canDownload ?? true;

  /** Index/source matching the document currently rendered by the Apryse viewer. */
  protected readonly loadedIndex = signal<number | null>(null);
  protected readonly loadedSource = signal<PreviewSource | null>(null);

  protected readonly isLoading = signal<boolean>(true);
  protected readonly loadError = signal<unknown | null>(null);

  /**
   * The backend's human-readable failure, when there is one: the Apollo error link re-throws
   * Hasura action errors as a GraphQLError, which Apollo hands to subscribers either directly or
   * wrapped as an ApolloError's networkError. Anything else (network, renderer) keeps the generic
   * error copy.
   */
  protected readonly loadErrorMessage = computed(() => {
    const error = this.loadError();
    if (error instanceof GraphQLError) return error.message || null;
    const wrapped = (error as { networkError?: unknown } | null)?.networkError;
    return wrapped instanceof GraphQLError ? wrapped.message || null : null;
  });

  /** Byte-level download progress from the blob processor; null when idle or unknown. */
  protected readonly downloadProgress = signal<{ loadedBytes: number; totalBytes: number | null } | null>(null);
  /** Whole-percent progress reported by Apryse's downloader for URL-streamed loads. */
  private readonly _rendererProgressPct = signal<number | null>(null);

  protected readonly progressPercent = computed(() => {
    const byteProgress = this.downloadProgress();
    if (byteProgress?.totalBytes) {
      return Math.min(100, Math.round((byteProgress.loadedBytes / byteProgress.totalBytes) * 100));
    }
    return this._rendererProgressPct();
  });

  protected readonly progressLabel = computed(() => {
    const byteProgress = this.downloadProgress();
    if (!byteProgress?.totalBytes) return null;
    return `${FileSizePipe.staticTransform(byteProgress.loadedBytes, 1)} of ${FileSizePipe.staticTransform(byteProgress.totalBytes, 1)}`;
  });

  protected readonly currentFile = computed(() => this.files()[this.currentIndex()]);
  protected readonly loadedFile = computed(() => {
    const idx = this.loadedIndex();
    return idx !== null ? this.files()[idx] : null;
  });
  protected readonly currentRenderer = computed(() => getRendererForExtension(this.currentFile()?.name));
  protected readonly showSidebar = computed(() => this.files().length > 1);
  /** Sidebar starts open; the header toggle collapses/expands it. */
  protected readonly sidebarCollapsed = signal<boolean>(false);
  protected readonly canPrint = computed(
    () => this.canDownload && this.currentRenderer() === 'apryse' && !this.isLoading() && !this.loadError() && this.loadedSource() !== null,
  );
  /** True while the renderer has a print job in flight — drives the spinner swap on the button. */
  protected readonly isPrinting = computed(() => !!this._apryseRenderer()?.printing?.());
  /** Disable Print when it isn't available, or while a job is already in flight. */
  protected readonly printDisabled = computed(() => !this.canPrint() || this.isPrinting());
  /**
   * Gate the Apryse renderer on the loaded source belonging to the *current* selection. `loadedIndex`
   * lags `currentIndex` during the async fetch after a switch, so without this check the renderer would
   * briefly mount against the previously-loaded (possibly errored) document and re-trigger its failure.
   */
  protected readonly isLoadedDocCurrent = computed(() => this.loadedSource() !== null && this.loadedIndex() === this.currentIndex());

  private readonly _apryseRenderer = viewChild(ApryseRendererComponent);

  private readonly _loadTrigger$ = new Subject<number>();
  private readonly _destroy$ = new Subject<void>();

  /** What kicked off the in-flight load — drives the `trigger` dimension on telemetry events. */
  private _trigger: LoadTrigger = 'initial';
  /** Whether the most recently resolved source came from the processor's cache (vs the network). */
  private _lastCacheHit = false;
  /** Guards the `closed` telemetry event so it fires exactly once on dialog teardown. */
  private _closedReported = false;

  /** Processor that resolved the source currently in flight/rendered — telemetry + fallback anchor. */
  private _activeProcessor: FilePreviewProcessor | null = null;
  /**
   * A URL the viewer can't load (CORS, Range support, signed-URL expiry) breaks for every file in
   * the session, not just one — after the first failure all loads route to the fallback processor.
   */
  private _urlStreamingBroken = false;

  constructor() {
    this._telemetry.previewOpened({
      fileCount: this.files().length,
      rendererKind: this.currentRenderer(),
      extension: getFileExtension(this.currentFile()?.name),
    });

    this._loadTrigger$
      .pipe(
        switchMap((index) => this.fetchForIndex(index)),
        takeUntil(this._destroy$),
      )
      .subscribe();

    this._loadTrigger$.next(this.currentIndex());
  }

  protected onToggleSidebar(): void {
    this.sidebarCollapsed.update((collapsed) => !collapsed);
  }

  protected onSelectFile(index: number): void {
    if (index === this.currentIndex()) return;
    const fromIndex = this.currentIndex();
    this._trigger = 'switch';
    // Marks 'switch' inside the service so the next render's endToEndMs is measured from here.
    this._telemetry.fileSwitched(fromIndex, index);
    this.currentIndex.set(index);
    this.loadError.set(null);
    this.isLoading.set(true);
    this._loadTrigger$.next(index);
  }

  protected onRetry(): void {
    this._trigger = 'retry';
    this._telemetry.retried({ extension: getFileExtension(this.currentFile()?.name), trigger: 'retry' });
    this.loadError.set(null);
    this.isLoading.set(true);
    this._loadTrigger$.next(this.currentIndex());
  }

  protected onRendererDocumentLoaded(meta: RenderedMeta): void {
    this.isLoading.set(false);
    const source = this.loadedSource();
    this._telemetry.documentRendered({
      index: this.currentIndex(),
      extension: getFileExtension(this.currentFile()?.name),
      sizeBytes: source?.kind === 'blob' ? source.blob.size : (this.loadedFile()?.fileInfo?.size ?? 0),
      pageCount: meta.pageCount,
      cacheHit: this._lastCacheHit,
      trigger: this._trigger,
      processor: this._activeProcessor?.kind,
    });
  }

  protected onRendererLoadingProgress(fraction: number): void {
    // Apryse reports a 0..1 fraction; normalize to a whole percent.
    this._rendererProgressPct.set(Math.min(100, Math.round(fraction <= 1 ? fraction * 100 : fraction)));
  }

  protected onClose(): void {
    this.reportClosed();
    this._dialogRef.close();
  }

  protected onRendererLoadError(payload: { stage: 'render' | 'instance'; error: unknown }): void {
    this._logger.error('FilePreviewComponent :: renderer load error', payload.error);
    this._telemetry.loadFailed({
      stage: payload.stage,
      extension: getFileExtension(this.currentFile()?.name),
      trigger: this._trigger,
      processor: this._activeProcessor?.kind,
      error: payload.error,
    });

    if (this.loadedSource()?.kind === 'url' && !this._urlStreamingBroken) {
      this._urlStreamingBroken = true;
      this._trigger = 'url-fallback';
      this.loadedSource.set(null);
      this.loadedIndex.set(null);
      this.isLoading.set(true);
      this._loadTrigger$.next(this.currentIndex());
      return;
    }

    this.loadError.set(payload.error);
    this.isLoading.set(false);
  }

  protected onPrint(): void {
    if (!this.canDownload) return;
    this._apryseRenderer()?.print();
  }

  protected onDownload(): void {
    const file = this.currentFile();
    if (!file || !this.canDownload) return;

    const extension = getFileExtension(file.name);
    const loaded = this.loadedFile() === file ? this.loadedSource() : null;
    if (loaded?.kind === 'blob') {
      // Reuses the already-loaded blob for the current file — no fetch, so this is a cache hit.
      this._telemetry.downloaded({ extension, sizeBytes: loaded.blob.size, cacheHit: true });
      this.triggerDownload(file, loaded.blob);
      return;
    }

    this.pickProcessor(file)
      .getDownloadBlob(file)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (blob) => {
          this._telemetry.downloaded({ extension, sizeBytes: blob.size, cacheHit: this._lastCacheHit });
          this.triggerDownload(file, blob);
        },
        error: (err) => {
          this._logger.error('FilePreviewComponent :: download fetch failed', err);
          this._telemetry.loadFailed({ stage: 'fetch', extension, trigger: this._trigger, error: err });
        },
      });
  }

  private pickProcessor(file: Partial<FileModel>): FilePreviewProcessor {
    const primary = this._processorFactory.getProcessor(file);
    if (this._urlStreamingBroken && primary.kind === 'url-streaming') {
      return this._processorFactory.getFallback(file, primary) ?? primary;
    }
    return primary;
  }

  private fetchForIndex(index: number): Observable<unknown> {
    const file = this.files()[index];
    if (!file) {
      this.isLoading.set(false);
      return EMPTY;
    }

    const extension = getFileExtension(file.name);

    if (!isSupportedExtension(file.name)) {
      // No fetch — we can't render this file. The unsupported renderer takes over.
      this.isLoading.set(false);
      this.loadError.set(null);
      this._telemetry.unsupported({ extension });
      return EMPTY;
    }

    this.isLoading.set(true);
    this.downloadProgress.set(null);
    this._rendererProgressPct.set(null);
    this._telemetry.mark('fetch');

    const processor = this.pickProcessor(file);
    this._activeProcessor = processor;

    return processor.resolveSource(file).pipe(
      tap((event) => {
        if (event.type === 'progress') {
          this.downloadProgress.set({ loadedBytes: event.loadedBytes, totalBytes: event.totalBytes });
          return;
        }
        this._lastCacheHit = event.cacheHit;
        this.downloadProgress.set(null);
        // Atomic swap: only update the displayed source once we have it. The spinner stays
        // up — `isLoading` is cleared by `onRendererDocumentLoaded()` once Apryse has
        // actually rendered the document, not the moment the source resolves.
        this.loadedSource.set(event.source);
        this.loadedIndex.set(index);
      }),
      catchError((err) => {
        this._logger.error('FilePreviewComponent :: source resolution failed', err);
        this.loadError.set(err);
        this.isLoading.set(false);
        this._telemetry.loadFailed({ stage: 'fetch', extension, trigger: this._trigger, processor: processor.kind, error: err });
        return EMPTY;
      }),
    );
  }

  private clampInitialIndex(index: number): number {
    const len = this._data.files.length;
    if (len === 0) return 0;
    return Math.min(Math.max(index, 0), len - 1);
  }

  private triggerDownload(file: Partial<FileModel>, blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name ?? 'file';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  /** Emits the Closed session-summary event exactly once (idempotent across onClose + ngOnDestroy). */
  private reportClosed(): void {
    if (this._closedReported) return;
    this._closedReported = true;
    this._telemetry.closed({ closedWhileLoading: this.isLoading(), closedOnError: !!this.loadError() });
  }

  ngOnDestroy(): void {
    // Covers dialog dismissal paths that bypass onClose (backdrop click, ESC, programmatic close).
    this.reportClosed();
    this._destroy$.next();
    this._destroy$.complete();
  }
}
